import { useState, useEffect } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { Link } from 'wouter';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { useTenant } from '@/contexts/TenantContext';
import GlobalHeader from '@/components/GlobalHeader';
import { 
  Brain, 
  TrendingUp, 
  Package, 
  AlertTriangle, 
  Zap, 
  Database,
  Activity,
  ArrowRight,
  CheckCircle2,
  XCircle,
  Clock,
  BarChart3,
  Sparkles,
  Info,
  RefreshCw,
  ArrowUpDown,
  ArrowUp,
  ArrowDown,
  Shield,
  Play,
  Loader2,
  Trophy,
  Swords,
  ChevronDown,
  ChevronUp
} from 'lucide-react';

/**
 * ML Command Center - Unified Dashboard
 * ======================================
 * Consolidates MLDashboard, ModelRegistry, and MLMonitoring into one view.
 * 
 * Sections:
 * 1. System Health Status (SageMaker endpoints)
 * 2. Summary Stats (models, accuracy, feature groups, endpoints)
 * 3. A/B Testing (Champion vs Challenger when active)
 * 4. All Registered Models table
 * 5. Feature Store Ingestion Status
 * 6. ML-Powered Features quick links
 */

interface ModelInfo {
  id: string;
  model_name: string;
  model_version?: string;
  retailer_id: string;
  retailer_name?: string;
  is_champion: boolean;
  is_challenger: boolean;
  mape_pct: number;
  traffic_pct: number;
  endpoint_name: string;
  endpoint_status?: string;
  is_live?: boolean;
  feature_count?: number;
  created_at: string;
}

interface EndpointHealth {
  name: string;
  retailer: string;
  status: 'InService' | 'Creating' | 'Updating' | 'Failed' | 'Deleting' | 'OutOfService' | 'Unknown';
  critical: boolean;
  healthy: boolean;
  lastChecked: string;
  errorMessage?: string;
}

interface SageMakerHealth {
  status: 'healthy' | 'degraded' | 'unhealthy';
  timestamp: string;
  awsConfigured: boolean;
  endpoints: EndpointHealth[];
  criticalEndpointsHealthy: number;
  criticalEndpointsTotal: number;
  message: string;
}

interface FeatureStoreHealth {
  status: 'healthy' | 'degraded' | 'down' | 'unknown';
  timestamp: string;
  ingestion: Array<{
    feature_group_name: string;
    last_success: string | null;
    failure_count_24h: number;
    avg_duration_seconds: number | null;
    records_processed_7d: number | null;
    health_status: 'healthy' | 'warning' | 'degraded' | 'stale';
  }>;
  performance: {
    avg_latency: number | null;
    p50_latency: number | null;
    p95_latency: number | null;
    p99_latency: number | null;
    error_count: number;
    total_reads: number;
    cache_hit_rate: number | null;
  };
  summary: {
    total_feature_groups: number;
    healthy_feature_groups: number;
    active_alerts: number;
    avg_api_latency_ms: number | null;
  };
}

type SortField = 'model_name' | 'retailer_name' | 'mape_pct' | 'traffic_pct' | 'endpoint_name' | 'created_at';
type SortDirection = 'asc' | 'desc';

export default function MLDashboard() {
  const { selectedRetailer } = useTenant();
  const retailerId = selectedRetailer?.id;
  const queryClient = useQueryClient();
  
  const [sortField, setSortField] = useState<SortField>('mape_pct');
  const [sortDirection, setSortDirection] = useState<SortDirection>('asc');
  const [showFeatureStore, setShowFeatureStore] = useState(false);
  const [syncing, setSyncing] = useState(false);
  const [syncResult, setSyncResult] = useState<any>(null);
  
  // Sort handlers
  const handleSort = (field: SortField) => {
    if (sortField === field) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortDirection('asc');
    }
  };
  
  const SortIcon = ({ field }: { field: SortField }) => {
    if (sortField !== field) return <ArrowUpDown className="h-3 w-3 ml-1 opacity-40" />;
    return sortDirection === 'asc' 
      ? <ArrowUp className="h-3 w-3 ml-1 text-purple-600" />
      : <ArrowDown className="h-3 w-3 ml-1 text-purple-600" />;
  };

  // Fetch model registry data (includes SageMaker endpoint status)
  const { data: registryData, isLoading: registryLoading, refetch: refetchRegistry } = useQuery({
    queryKey: ['ml-registry', retailerId],
    queryFn: async () => {
      const url = retailerId 
        ? `/api/ml/registry?retailer_id=${retailerId}&include_accuracy=true`
        : '/api/ml/registry?include_accuracy=true';
      const res = await fetch(url);
      if (!res.ok) throw new Error('Failed to fetch registry');
      return res.json();
    },
    staleTime: 60000,
  });

  // Fetch SageMaker health
  const { data: sagemakerHealth, isLoading: sagemakerLoading, refetch: refetchSagemaker } = useQuery<SageMakerHealth>({
    queryKey: ['sagemaker-health'],
    queryFn: async () => {
      const res = await fetch('/api/ml/health');
      if (!res.ok) throw new Error('Failed to fetch SageMaker health');
      return res.json();
    },
    staleTime: 30000,
  });

  // Fetch Feature Store health
  const { data: featureStoreHealth, isLoading: featureStoreLoading, refetch: refetchFeatureStore } = useQuery<FeatureStoreHealth>({
    queryKey: ['feature-store-health'],
    queryFn: async () => {
      const res = await fetch('/api/ml/monitoring/feature-store-health');
      if (!res.ok) throw new Error('Failed to fetch Feature Store health');
      return res.json();
    },
    staleTime: 60000,
  });

  // Refresh all data
  const refreshAll = () => {
    refetchRegistry();
    refetchSagemaker();
    refetchFeatureStore();
  };

  // Weekly sync trigger
  const triggerSync = async () => {
    setSyncing(true);
    setSyncResult(null);
    
    try {
      const retailers = ['ulta', 'macys', 'jcp'];
      const results = [];
      
      for (const r of retailers) {
        try {
          const res = await fetch(`/api/ml/weekly-sync?retailer=${r}`, { method: 'POST' });
          const data = await res.json();
          results.push({ ...data, retailer: r });
        } catch (err: any) {
          results.push({ error: err.message, retailer: r });
        }
      }
      
      const totalWeeks = results.reduce((sum, r) => sum + (r.new_weeks_ingested?.length || 0), 0);
      const errors = results.filter(r => r.error);
      
      setSyncResult({
        success: errors.length === 0,
        results,
        summary: {
          retailers_synced: retailers.length,
          total_new_weeks: totalWeeks,
          errors: errors.length
        },
        message: errors.length > 0 
          ? `Synced ${totalWeeks} weeks with ${errors.length} errors`
          : `Successfully synced all ${retailers.length} retailers (${totalWeeks} new weeks)`
      });
      
      // Refresh all data after sync
      refreshAll();
    } catch (err: any) {
      setSyncResult({ error: err.message });
    } finally {
      setSyncing(false);
    }
  };

  // Clear sync result after 10 seconds
  useEffect(() => {
    if (syncResult) {
      const timer = setTimeout(() => setSyncResult(null), 10000);
      return () => clearTimeout(timer);
    }
  }, [syncResult]);

  const rawModels: ModelInfo[] = registryData?.models || [];
  
  // Sort models
  const models = [...rawModels].sort((a, b) => {
    let aVal: any, bVal: any;
    
    switch (sortField) {
      case 'model_name':
      case 'retailer_name':
      case 'endpoint_name':
        aVal = (a[sortField] || '').toLowerCase();
        bVal = (b[sortField] || '').toLowerCase();
        break;
      case 'created_at':
        aVal = new Date(a.created_at).getTime();
        bVal = new Date(b.created_at).getTime();
        break;
      default:
        aVal = a[sortField] || 0;
        bVal = b[sortField] || 0;
    }
    
    if (aVal < bVal) return sortDirection === 'asc' ? -1 : 1;
    if (aVal > bVal) return sortDirection === 'asc' ? 1 : -1;
    return 0;
  });
  
  const championModels = models.filter((m: ModelInfo) => m.is_champion);
  const challengerModels = models.filter((m: ModelInfo) => m.is_challenger);
  const liveModels = models.filter((m: ModelInfo) => m.is_live || m.endpoint_status === 'InService');
  const avgMape = championModels.length > 0 
    ? championModels.reduce((sum: number, m: ModelInfo) => sum + (Number(m.mape_pct) || 0), 0) / championModels.length 
    : 0;

  const getMapeColor = (mape: number) => {
    if (mape < 10) return 'text-green-600';
    if (mape < 20) return 'text-yellow-600';
    return 'text-red-600';
  };

  const formatDate = (dateStr: string) => {
    if (!dateStr) return 'N/A';
    return new Date(dateStr).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
    });
  };

  const formatTimestamp = (timestamp: string | null) => {
    if (!timestamp) return 'Never';
    const date = new Date(timestamp);
    const now = new Date();
    const diffMs = now.getTime() - date.getTime();
    const diffMins = Math.floor(diffMs / 60000);
    
    if (diffMins < 1) return 'Just now';
    if (diffMins < 60) return `${diffMins}m ago`;
    if (diffMins < 1440) return `${Math.floor(diffMins / 60)}h ago`;
    return `${Math.floor(diffMins / 1440)}d ago`;
  };

  const mlFeatures = [
    {
      title: 'Sales Forecaster',
      description: 'Weekly revenue predictions using XGBoost ML models',
      href: '/ai/sales-forecaster',
      icon: TrendingUp,
      gradient: 'from-emerald-500 to-teal-500',
    },
    {
      title: 'SKU Demand Forecaster',
      description: 'Per-SKU unit predictions with velocity ranking',
      href: '/ml/sku-demand',
      icon: Package,
      gradient: 'from-blue-500 to-cyan-500',
    },
    {
      title: 'Stockout Predictor',
      description: 'Proactive stockout risk detection',
      href: '/ml/stockout',
      icon: AlertTriangle,
      gradient: 'from-orange-500 to-amber-500',
    },
    {
      title: 'Forecast Lab',
      description: 'Human-in-the-loop anomaly detection & feedback',
      href: '/ai/forecast-anomalies',
      icon: Zap,
      gradient: 'from-purple-500 to-pink-500',
    },
  ];

  const isLoading = registryLoading || sagemakerLoading || featureStoreLoading;

  return (
    <div className="min-h-screen bg-gray-50">
      <GlobalHeader 
        title="ML Command Center"
        subtitle="Enterprise ML infrastructure • Real-time monitoring & A/B testing"
      />
      
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8 space-y-6">
        
        {/* SageMaker Health Status Banner */}
        {sagemakerHealth && (
          <div className={`rounded-xl shadow-sm border-2 ${
            sagemakerHealth.status === 'healthy' 
              ? 'bg-gradient-to-r from-green-50 to-emerald-50 border-green-200'
              : sagemakerHealth.status === 'degraded'
              ? 'bg-gradient-to-r from-yellow-50 to-amber-50 border-yellow-300'
              : 'bg-gradient-to-r from-red-50 to-rose-50 border-red-300'
          }`}>
            <div className="p-4">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <div className={`p-2 rounded-lg ${
                    sagemakerHealth.status === 'healthy' ? 'bg-green-500' :
                    sagemakerHealth.status === 'degraded' ? 'bg-yellow-500' : 'bg-red-500'
                  }`}>
                    <Zap className="w-5 h-5 text-white" />
                  </div>
                  <div>
                    <h3 className="font-semibold text-gray-900 flex items-center gap-2">
                      SageMaker Endpoint Health
                      <Badge className={`text-xs ${
                        sagemakerHealth.status === 'healthy' ? 'bg-green-100 text-green-700' :
                        sagemakerHealth.status === 'degraded' ? 'bg-yellow-100 text-yellow-700' : 'bg-red-100 text-red-700'
                      }`}>
                        {sagemakerHealth.status.toUpperCase()}
                      </Badge>
                    </h3>
                    <p className="text-sm text-gray-600">
                      {sagemakerHealth.criticalEndpointsHealthy}/{sagemakerHealth.criticalEndpointsTotal} critical endpoints healthy
                    </p>
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  <Button 
                    variant="outline" 
                    size="sm" 
                    onClick={() => triggerSync()}
                    disabled={syncing}
                    className="bg-white"
                  >
                    {syncing ? (
                      <><Loader2 className="h-4 w-4 mr-2 animate-spin" />Syncing...</>
                    ) : (
                      <><Play className="h-4 w-4 mr-2" />Weekly Sync</>
                    )}
                  </Button>
                  <Button variant="outline" size="sm" onClick={refreshAll} className="bg-white">
                    <RefreshCw className={`h-4 w-4 mr-2 ${isLoading ? 'animate-spin' : ''}`} />
                    Refresh
                  </Button>
                </div>
              </div>
              
              {/* Endpoint Grid */}
              {sagemakerHealth.awsConfigured && sagemakerHealth.endpoints.length > 0 && (
                <div className="mt-4 grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3">
                  {sagemakerHealth.endpoints.map((ep, idx) => (
                    <div 
                      key={idx}
                      className={`p-3 rounded-lg border ${
                        ep.healthy ? 'bg-white border-green-200' : 'bg-red-50 border-red-300'
                      }`}
                    >
                      <div className="flex items-center justify-between mb-1">
                        <span className="font-medium text-gray-900 text-sm">{ep.retailer}</span>
                        {ep.critical && (
                          <span className={`text-xs px-1.5 py-0.5 rounded font-medium ${
                            ep.healthy ? 'bg-blue-100 text-blue-700' : 'bg-red-100 text-red-700'
                          }`}>
                            {ep.healthy ? 'PRIORITY' : 'DOWN'}
                          </span>
                        )}
                      </div>
                      <div className="flex items-center gap-1.5">
                        {ep.healthy ? (
                          <CheckCircle2 className="w-4 h-4 text-green-600" />
                        ) : (
                          <XCircle className="w-4 h-4 text-red-600" />
                        )}
                        <span className={`text-sm font-medium ${ep.healthy ? 'text-green-700' : 'text-red-700'}`}>
                          {ep.status}
                        </span>
                      </div>
                      <p className="text-xs text-gray-500 font-mono mt-1 truncate" title={ep.name}>
                        {ep.name}
                      </p>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        )}

        {/* Sync Result Banner */}
        {syncResult && (
          <div className={`p-4 rounded-lg border-2 ${
            syncResult.error 
              ? 'bg-gradient-to-r from-red-50 to-orange-50 border-red-200' 
              : 'bg-gradient-to-r from-green-50 to-emerald-50 border-green-200'
          }`}>
            <div className="flex items-start gap-3">
              <div className={`p-2 rounded-lg ${syncResult.error ? 'bg-red-500' : 'bg-green-500'}`}>
                {syncResult.error ? (
                  <XCircle className="w-5 h-5 text-white" />
                ) : (
                  <CheckCircle2 className="w-5 h-5 text-white" />
                )}
              </div>
              <div className="flex-1">
                <div className="font-bold">
                  {syncResult.error ? 'Sync Failed' : 'Sync Completed Successfully'}
                </div>
                {syncResult.error ? (
                  <div className="text-sm text-red-700">{syncResult.error}</div>
                ) : (
                  <div className="text-sm text-green-700">
                    ✓ Synced {syncResult.summary?.retailers_synced || 0} retailers • {syncResult.summary?.total_new_weeks || 0} new weeks ingested
                  </div>
                )}
              </div>
              <button onClick={() => setSyncResult(null)} className="text-gray-400 hover:text-gray-600">
                <XCircle className="w-4 h-4" />
              </button>
            </div>
          </div>
        )}

        {/* Stats Cards */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
          <div className="bg-gradient-to-br from-emerald-50 to-emerald-100/50 border border-emerald-200 rounded-xl p-6 shadow-sm hover:shadow-lg transition-shadow">
            <div className="flex items-center justify-between mb-4">
              <span className="text-sm font-medium text-emerald-700">Live Endpoints</span>
              <div className="p-3 bg-emerald-500 rounded-xl shadow-md">
                <CheckCircle2 className="h-5 w-5 text-white" />
              </div>
            </div>
            <div className="text-4xl font-bold text-emerald-700 mb-2">
              {isLoading ? <span className="text-2xl">Loading...</span> : liveModels.length}
            </div>
            <div className="flex items-center gap-1 text-sm text-emerald-600">
              <Sparkles className="h-3 w-3" />
              <span className="font-medium">{championModels.length} champion{championModels.length !== 1 ? 's' : ''}</span>
            </div>
          </div>

          <div className="bg-gradient-to-br from-blue-50 to-blue-100/50 border border-blue-200 rounded-xl p-6 shadow-sm hover:shadow-lg transition-shadow">
            <div className="flex items-center justify-between mb-4">
              <span className="text-sm font-medium text-blue-700">Avg. MAPE</span>
              <div className="p-3 bg-blue-500 rounded-xl shadow-md">
                <BarChart3 className="h-5 w-5 text-white" />
              </div>
            </div>
            <div className={`text-4xl font-bold mb-2 ${getMapeColor(avgMape)}`}>
              {isLoading ? <span className="text-2xl text-blue-700">Loading...</span> : `${avgMape.toFixed(1)}%`}
            </div>
            <div className="flex items-center gap-1 text-sm text-blue-600">
              <TrendingUp className="h-3 w-3" />
              <span className="font-medium">Lower is better</span>
            </div>
          </div>

          <div className="bg-gradient-to-br from-purple-50 to-purple-100/50 border border-purple-200 rounded-xl p-6 shadow-sm hover:shadow-lg transition-shadow">
            <div className="flex items-center justify-between mb-4">
              <span className="text-sm font-medium text-purple-700">Feature Groups</span>
              <div className="p-3 bg-purple-500 rounded-xl shadow-md">
                <Database className="h-5 w-5 text-white" />
              </div>
            </div>
            <div className="text-4xl font-bold text-purple-700 mb-2">
              {isLoading ? <span className="text-2xl">Loading...</span> : featureStoreHealth?.summary?.total_feature_groups || 0}
            </div>
            <div className="flex items-center gap-1 text-sm text-purple-600">
              {featureStoreHealth?.status === 'healthy' ? (
                <>
                  <CheckCircle2 className="h-3 w-3" />
                  <span className="font-medium">All healthy</span>
                </>
              ) : (
                <>
                  <AlertTriangle className="h-3 w-3" />
                  <span className="font-medium">{featureStoreHealth?.status || 'Checking...'}</span>
                </>
              )}
            </div>
          </div>

          <div className="bg-gradient-to-br from-orange-50 to-orange-100/50 border border-orange-200 rounded-xl p-6 shadow-sm hover:shadow-lg transition-shadow">
            <div className="flex items-center justify-between mb-4">
              <span className="text-sm font-medium text-orange-700">Total Models</span>
              <div className="p-3 bg-orange-500 rounded-xl shadow-md">
                <Brain className="h-5 w-5 text-white" />
              </div>
            </div>
            <div className="text-4xl font-bold text-orange-700 mb-2">
              {isLoading ? <span className="text-2xl">Loading...</span> : models.length}
            </div>
            <div className="flex items-center gap-1 text-sm text-orange-600">
              <Zap className="h-3 w-3" />
              <span className="font-medium">{challengerModels.length} challenger{challengerModels.length !== 1 ? 's' : ''}</span>
            </div>
          </div>
        </div>

        {/* Champion vs Challenger Comparison */}
        {championModels.length > 0 && challengerModels.length > 0 && (
          <Card className="border-indigo-200 shadow-md">
            <CardHeader className="bg-gradient-to-r from-indigo-50 to-violet-50 border-b border-indigo-100">
              <div className="flex items-center justify-between">
                <div>
                  <CardTitle className="flex items-center gap-2 text-lg">
                    <Swords className="h-6 w-6 text-indigo-600" />
                    A/B Testing: Champion vs Challenger
                  </CardTitle>
                  <CardDescription>Active model comparison</CardDescription>
                </div>
                <Badge className="bg-gradient-to-r from-indigo-500 to-violet-600 text-white">
                  <Activity className="h-3 w-3 mr-1" />
                  Live A/B Test
                </Badge>
              </div>
            </CardHeader>
            <CardContent className="pt-6">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                {/* Champion Side */}
                <div className="p-6 rounded-xl border-2 border-amber-300 bg-gradient-to-br from-amber-50 to-amber-100/50">
                  <div className="flex items-center gap-2 mb-4">
                    <div className="p-2 bg-amber-500 rounded-lg">
                      <Trophy className="h-5 w-5 text-white" />
                    </div>
                    <span className="font-bold text-amber-700 text-lg">Champion</span>
                    <Badge className="bg-amber-500 ml-auto">{championModels[0]?.traffic_pct || 0}% Traffic</Badge>
                  </div>
                  <div className="space-y-3">
                    <div>
                      <p className="text-xs text-amber-600 font-medium uppercase">Model</p>
                      <p className="font-semibold text-gray-900">{championModels[0]?.model_name}</p>
                    </div>
                    <div>
                      <p className="text-xs text-amber-600 font-medium uppercase">MAPE Score</p>
                      <p className={`text-3xl font-bold ${getMapeColor(Number(championModels[0]?.mape_pct) || 0)}`}>
                        {championModels[0]?.mape_pct ? Number(championModels[0].mape_pct).toFixed(1) : 'N/A'}%
                      </p>
                    </div>
                  </div>
                </div>

                {/* Challenger Side */}
                <div className="p-6 rounded-xl border-2 border-blue-300 bg-gradient-to-br from-blue-50 to-blue-100/50">
                  <div className="flex items-center gap-2 mb-4">
                    <div className="p-2 bg-blue-500 rounded-lg">
                      <Swords className="h-5 w-5 text-white" />
                    </div>
                    <span className="font-bold text-blue-700 text-lg">Challenger</span>
                    <Badge variant="outline" className="border-blue-500 text-blue-700 bg-blue-50 ml-auto">
                      {challengerModels[0]?.traffic_pct || 0}% Traffic
                    </Badge>
                  </div>
                  <div className="space-y-3">
                    <div>
                      <p className="text-xs text-blue-600 font-medium uppercase">Model</p>
                      <p className="font-semibold text-gray-900">{challengerModels[0]?.model_name}</p>
                    </div>
                    <div>
                      <p className="text-xs text-blue-600 font-medium uppercase">MAPE Score</p>
                      <p className={`text-3xl font-bold ${getMapeColor(Number(challengerModels[0]?.mape_pct) || 0)}`}>
                        {challengerModels[0]?.mape_pct ? Number(challengerModels[0].mape_pct).toFixed(1) : 'N/A'}%
                      </p>
                    </div>
                  </div>
                </div>
              </div>

              {/* Comparison Result */}
              <div className="mt-6 p-4 rounded-lg bg-gradient-to-r from-indigo-50 to-violet-50 border border-indigo-200 flex items-center justify-center gap-3">
                {Number(challengerModels[0]?.mape_pct) < Number(championModels[0]?.mape_pct) ? (
                  <>
                    <div className="p-2 bg-green-500 rounded-full">
                      <TrendingUp className="h-5 w-5 text-white" />
                    </div>
                    <span className="font-bold text-gray-900">
                      Challenger outperforming by {((Number(championModels[0]?.mape_pct) || 0) - (Number(challengerModels[0]?.mape_pct) || 0)).toFixed(1)}% MAPE
                    </span>
                    <Badge className="bg-gradient-to-r from-green-500 to-emerald-600 text-white">
                      <Sparkles className="h-3 w-3 mr-1" />
                      Promotion Candidate
                    </Badge>
                  </>
                ) : (
                  <>
                    <div className="p-2 bg-amber-500 rounded-full">
                      <Trophy className="h-5 w-5 text-white" />
                    </div>
                    <span className="font-semibold text-gray-900">
                      Champion leading by {((Number(challengerModels[0]?.mape_pct) || 0) - (Number(championModels[0]?.mape_pct) || 0)).toFixed(1)}% MAPE
                    </span>
                    <Badge variant="outline" className="border-amber-500 text-amber-700 bg-amber-50">
                      Champion Defending
                    </Badge>
                  </>
                )}
              </div>
            </CardContent>
          </Card>
        )}

        {/* All Models Table */}
        <Card className="border-gray-200 shadow-sm">
          <CardHeader className="border-b border-gray-100 bg-gradient-to-r from-gray-50 to-transparent">
            <div className="flex items-center justify-between">
              <div>
                <CardTitle className="text-lg flex items-center gap-3">
                  <Database className="h-6 w-6 text-purple-600" />
                  All Registered Models
                </CardTitle>
                <CardDescription>Complete model inventory with live endpoint status</CardDescription>
              </div>
            </div>
          </CardHeader>
          <CardContent className="pt-6">
            {isLoading ? (
              <div className="flex flex-col items-center justify-center py-16 text-muted-foreground">
                <div className="p-5 bg-purple-100 rounded-full mb-4">
                  <Clock className="h-10 w-10 text-purple-600 animate-spin" />
                </div>
                <p className="font-medium">Loading models...</p>
                <p className="text-sm">Syncing with SageMaker</p>
              </div>
            ) : models.length === 0 ? (
              <div className="flex flex-col items-center justify-center py-16 text-muted-foreground">
                <div className="p-5 bg-gray-100 rounded-full mb-4">
                  <XCircle className="h-10 w-10 text-gray-400" />
                </div>
                <p className="font-medium">No models registered yet</p>
                <p className="text-sm">Deploy your first model to get started</p>
              </div>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead>
                    <tr className="border-b border-gray-200 bg-gray-50/50">
                      <th 
                        className="text-left py-3 px-4 font-semibold text-gray-700 text-sm cursor-pointer hover:bg-gray-100 transition-colors select-none"
                        onClick={() => handleSort('model_name')}
                      >
                        <div className="flex items-center">Model<SortIcon field="model_name" /></div>
                      </th>
                      <th 
                        className="text-left py-3 px-4 font-semibold text-gray-700 text-sm cursor-pointer hover:bg-gray-100 transition-colors select-none"
                        onClick={() => handleSort('retailer_name')}
                      >
                        <div className="flex items-center">Retailer<SortIcon field="retailer_name" /></div>
                      </th>
                      <th className="text-left py-3 px-4 font-semibold text-gray-700 text-sm">Role</th>
                      <th className="text-left py-3 px-4 font-semibold text-gray-700 text-sm">Status</th>
                      <th 
                        className="text-left py-3 px-4 font-semibold text-gray-700 text-sm cursor-pointer hover:bg-gray-100 transition-colors select-none"
                        onClick={() => handleSort('mape_pct')}
                      >
                        <div className="flex items-center">MAPE<SortIcon field="mape_pct" /></div>
                      </th>
                      <th 
                        className="text-left py-3 px-4 font-semibold text-gray-700 text-sm cursor-pointer hover:bg-gray-100 transition-colors select-none"
                        onClick={() => handleSort('traffic_pct')}
                      >
                        <div className="flex items-center">Traffic<SortIcon field="traffic_pct" /></div>
                      </th>
                      <th className="text-left py-3 px-4 font-semibold text-gray-700 text-sm">Features</th>
                      <th 
                        className="text-left py-3 px-4 font-semibold text-gray-700 text-sm cursor-pointer hover:bg-gray-100 transition-colors select-none"
                        onClick={() => handleSort('created_at')}
                      >
                        <div className="flex items-center">Created<SortIcon field="created_at" /></div>
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {models.map((model: ModelInfo, idx: number) => (
                      <tr key={idx} className={`border-b border-gray-100 last:border-0 hover:bg-purple-50/30 transition-colors ${
                        model.is_champion ? 'bg-amber-500/5' : model.is_challenger ? 'bg-blue-500/5' : ''
                      }`}>
                        <td className="py-4 px-4">
                          <div className="flex items-center gap-2">
                            <Brain className="h-4 w-4 text-gray-400" />
                            <span className="font-medium text-gray-900">{model.model_name}</span>
                            {model.model_version && (
                              <span className="text-xs text-gray-500">v{model.model_version}</span>
                            )}
                          </div>
                        </td>
                        <td className="py-4 px-4 text-sm text-gray-700">
                          {model.retailer_name || 'Unknown'}
                        </td>
                        <td className="py-4 px-4">
                          {model.is_champion ? (
                            <Badge className="bg-gradient-to-r from-amber-500 to-amber-600 text-white shadow-sm">
                              <Trophy className="h-3 w-3 mr-1" />
                              Champion
                            </Badge>
                          ) : model.is_challenger ? (
                            <Badge className="bg-gradient-to-r from-blue-500 to-indigo-600 text-white shadow-sm">
                              <Swords className="h-3 w-3 mr-1" />
                              Challenger
                            </Badge>
                          ) : (
                            <Badge variant="secondary" className="text-gray-600">
                              Inactive
                            </Badge>
                          )}
                        </td>
                        <td className="py-4 px-4">
                          {model.endpoint_status === 'InService' || model.is_live ? (
                            <Badge variant="outline" className="border-green-500 text-green-700 bg-green-50">
                              <div className="h-2 w-2 bg-green-500 rounded-full mr-1.5 animate-pulse" />
                              Live
                            </Badge>
                          ) : model.endpoint_status === 'Failed' ? (
                            <Badge variant="outline" className="border-red-500 text-red-700 bg-red-50">
                              <XCircle className="h-3 w-3 mr-1" />
                              Failed
                            </Badge>
                          ) : model.endpoint_status === 'Creating' || model.endpoint_status === 'Updating' ? (
                            <Badge variant="outline" className="border-yellow-500 text-yellow-700 bg-yellow-50">
                              <Clock className="h-3 w-3 mr-1 animate-spin" />
                              {model.endpoint_status}
                            </Badge>
                          ) : (
                            <Badge variant="outline" className="border-gray-300 text-gray-600">
                              {model.endpoint_status || 'Unknown'}
                            </Badge>
                          )}
                        </td>
                        <td className="py-4 px-4">
                          <span className={`font-semibold ${getMapeColor(Number(model.mape_pct))}`}>
                            {model.mape_pct ? Number(model.mape_pct).toFixed(1) : 'N/A'}%
                          </span>
                        </td>
                        <td className="py-4 px-4">
                          <span className="font-medium text-gray-700">{model.traffic_pct}%</span>
                        </td>
                        <td className="py-4 px-4 text-sm text-gray-600">
                          {model.feature_count || 'N/A'}
                        </td>
                        <td className="py-4 px-4 text-sm text-gray-500">
                          {formatDate(model.created_at)}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Feature Store Ingestion Status (Collapsible) */}
        <Card className="border-gray-200 shadow-sm">
          <CardHeader 
            className="cursor-pointer hover:bg-gray-50 transition-colors"
            onClick={() => setShowFeatureStore(!showFeatureStore)}
          >
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <Database className="h-5 w-5 text-purple-600" />
                <div>
                  <CardTitle className="text-base">Feature Store Ingestion Status</CardTitle>
                  <CardDescription>
                    {featureStoreHealth?.summary?.healthy_feature_groups || 0}/{featureStoreHealth?.summary?.total_feature_groups || 0} feature groups healthy
                  </CardDescription>
                </div>
              </div>
              <Button variant="ghost" size="sm">
                {showFeatureStore ? <ChevronUp className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />}
              </Button>
            </div>
          </CardHeader>
          {showFeatureStore && featureStoreHealth && (
            <CardContent className="pt-0">
              {featureStoreHealth.ingestion.length === 0 ? (
                <div className="text-center py-8 text-gray-500">
                  <Database className="w-12 h-12 mx-auto mb-2 opacity-50" />
                  <p>No feature groups configured yet</p>
                </div>
              ) : (
                <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead>
                      <tr className="border-b">
                        <th className="text-left p-3 text-sm font-medium text-gray-700">Status</th>
                        <th className="text-left p-3 text-sm font-medium text-gray-700">Feature Group</th>
                        <th className="text-left p-3 text-sm font-medium text-gray-700">Last Success</th>
                        <th className="text-left p-3 text-sm font-medium text-gray-700">24h Failures</th>
                        <th className="text-left p-3 text-sm font-medium text-gray-700">Avg Duration</th>
                        <th className="text-left p-3 text-sm font-medium text-gray-700">Records (7d)</th>
                      </tr>
                    </thead>
                    <tbody>
                      {featureStoreHealth.ingestion.map((fg, idx) => (
                        <tr key={idx} className="border-b hover:bg-gray-50">
                          <td className="p-3">
                            <Badge className={`text-xs ${
                              fg.health_status === 'healthy' ? 'bg-green-100 text-green-700' :
                              fg.health_status === 'warning' ? 'bg-yellow-100 text-yellow-700' :
                              fg.health_status === 'stale' ? 'bg-red-100 text-red-700' :
                              'bg-gray-100 text-gray-700'
                            }`}>
                              {fg.health_status}
                            </Badge>
                          </td>
                          <td className="p-3 font-mono text-sm">{fg.feature_group_name}</td>
                          <td className="p-3 text-sm text-gray-600">{formatTimestamp(fg.last_success)}</td>
                          <td className="p-3">
                            <span className={`font-semibold ${fg.failure_count_24h > 0 ? 'text-red-600' : 'text-gray-400'}`}>
                              {fg.failure_count_24h}
                            </span>
                          </td>
                          <td className="p-3 text-sm text-gray-600">
                            {fg.avg_duration_seconds ? `${fg.avg_duration_seconds.toFixed(0)}s` : 'N/A'}
                          </td>
                          <td className="p-3 text-sm text-gray-600">
                            {fg.records_processed_7d?.toLocaleString() || 'N/A'}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}

              {/* API Performance Summary */}
              {featureStoreHealth.performance && (
                <div className="mt-4 pt-4 border-t">
                  <p className="text-sm font-medium text-gray-700 mb-3">API Performance (Last Hour)</p>
                  <div className="grid grid-cols-5 gap-4">
                    <div className="text-center">
                      <div className="text-lg font-bold text-gray-900">
                        {featureStoreHealth.performance.avg_latency?.toFixed(0) || 0}ms
                      </div>
                      <div className="text-xs text-gray-500">Avg Latency</div>
                    </div>
                    <div className="text-center">
                      <div className="text-lg font-bold text-gray-900">
                        {featureStoreHealth.performance.p50_latency?.toFixed(0) || 0}ms
                      </div>
                      <div className="text-xs text-gray-500">P50</div>
                    </div>
                    <div className="text-center">
                      <div className="text-lg font-bold text-gray-900">
                        {featureStoreHealth.performance.p95_latency?.toFixed(0) || 0}ms
                      </div>
                      <div className="text-xs text-gray-500">P95</div>
                    </div>
                    <div className="text-center">
                      <div className="text-lg font-bold text-gray-900">
                        {featureStoreHealth.performance.total_reads}
                      </div>
                      <div className="text-xs text-gray-500">Total Reads</div>
                    </div>
                    <div className="text-center">
                      <div className={`text-lg font-bold ${featureStoreHealth.performance.cache_hit_rate ? 'text-green-600' : 'text-gray-900'}`}>
                        {featureStoreHealth.performance.cache_hit_rate?.toFixed(0) || 0}%
                      </div>
                      <div className="text-xs text-gray-500">Cache Hit</div>
                    </div>
                  </div>
                </div>
              )}
            </CardContent>
          )}
        </Card>

        {/* ML-Powered Features Quick Links */}
        <div>
          <h2 className="text-lg font-semibold text-gray-900 mb-4">ML-Powered Features</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
            {mlFeatures.map((feature) => {
              const Icon = feature.icon;
              return (
                <Link key={feature.href} href={feature.href}>
                  <div className="bg-white border border-gray-200 rounded-xl p-5 shadow-sm hover:shadow-lg hover:scale-[1.02] hover:border-purple-300 transition-all duration-200 cursor-pointer">
                    <div className="flex items-center gap-3 mb-3">
                      <div className={`p-2 rounded-lg bg-gradient-to-br ${feature.gradient}`}>
                        <Icon className="h-5 w-5 text-white" />
                      </div>
                      <h3 className="font-semibold text-gray-900">{feature.title}</h3>
                    </div>
                    <p className="text-sm text-gray-600">{feature.description}</p>
                    <div className="flex items-center text-sm font-medium text-purple-600 mt-3">
                      <span>Open</span>
                      <ArrowRight className="h-4 w-4 ml-1" />
                    </div>
                  </div>
                </Link>
              );
            })}
          </div>
        </div>
      </div>
    </div>
  );
}
